import React, { useState, useEffect } from 'react';
import { getGroups, ApiGroup } from '../../services/GroupApi';
import { getContacts, ApiContact } from '../../services/ContactApi';

interface Group {
  id: string;
  name: string;
  contactCount: number;
}

interface GroupsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contacts: string[]) => void;
}

function getCurrentUserId(): string | null {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.id || null;
  } catch (_) {
    return null;
  }
}

export const GroupsModal: React.FC<GroupsModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadGroups();
      setSelectedGroups([]);
    }
  }, [isOpen]);

  const loadGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get current user ID and pass it to filter groups
      const userId = getCurrentUserId();
      const apiGroups = await getGroups(userId ? { userId } : undefined);
      const mappedGroups: Group[] = apiGroups.map((g: ApiGroup) => ({
        id: g.id,
        name: g.name,
        contactCount: g.contactCount || 0
      }));
      setGroups(mappedGroups);
    } catch (err: any) {
      console.error('Failed to load groups:', err);
      setError(err?.message || 'Failed to load groups. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAllGroups = () => {
    if (selectedGroups.length === groups.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(groups.map(group => group.id));
    }
  };

  const loadGroupContacts = async (groupNames: string[]): Promise<string[]> => {
    // Get all contacts from the API
    const allContacts = await getContacts();
    
    // Filter contacts by group names (case-insensitive)
    const groupPhoneNumbers: string[] = [];
    
    groupNames.forEach(groupName => {
      const normalizedGroupName = groupName.trim().toLowerCase();
      const matchingContacts = allContacts.filter((contact: ApiContact) => {
        if (!contact || !contact.group || !contact.phone) return false;
        const contactGroup = String(contact.group).trim().toLowerCase();
        return contactGroup === normalizedGroupName;
      });
      
      // Extract phone numbers and add to list (avoid duplicates)
      matchingContacts.forEach((contact: ApiContact) => {
        const phone = contact.phone?.trim();
        if (phone && phone.length > 0 && !groupPhoneNumbers.includes(phone)) {
          groupPhoneNumbers.push(phone);
        }
      });
    });

    return groupPhoneNumbers;
  };

  const handleGroupClick = async (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    setIsLoadingContacts(true);
    setError(null);
    
    try {
      const phoneNumbers = await loadGroupContacts([group.name]);
      
      if (phoneNumbers.length === 0) {
        setError(`No phone numbers found in group "${group.name}".`);
        setIsLoadingContacts(false);
        return;
      }

      onSave(phoneNumbers);
      onClose();
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
      setError(err?.message || 'Failed to load contacts from group. Please try again.');
      setIsLoadingContacts(false);
    }
  };

  const handleSave = async () => {
    if (selectedGroups.length === 0) {
      return;
    }

    setIsLoadingContacts(true);
    setError(null);
    
    try {
      // Get selected group names
      const selectedGroupNames = selectedGroups.map(groupId => {
        const group = groups.find(g => g.id === groupId);
        return group ? group.name : null;
      }).filter(name => name !== null) as string[];

      const groupPhoneNumbers = await loadGroupContacts(selectedGroupNames);

      if (groupPhoneNumbers.length === 0) {
        setError('No phone numbers found in the selected groups.');
        setIsLoadingContacts(false);
        return;
      }

      onSave(groupPhoneNumbers);
      onClose();
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
      setError(err?.message || 'Failed to load contacts from selected groups. Please try again.');
    } finally {
      setIsLoadingContacts(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Contact Groups</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Select All Groups Option */}
        <div className="mb-4">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              checked={selectedGroups.length === groups.length}
              onChange={handleSelectAllGroups}
              className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
            />
            <span className="text-sm font-medium text-gray-700">
              Add all groups ({groups.length})
            </span>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5722]"></div>
          </div>
        ) : (
          /* Groups List */
          <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
            {groups.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No groups found</p>
            ) : (
              <div className="space-y-2">
                {groups.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={(e) => {
                      // Don't trigger if clicking the checkbox
                      if ((e.target as HTMLElement).tagName !== 'INPUT') {
                        handleGroupClick(group.id);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.id)}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent row click
                        if (e.target.checked) {
                          setSelectedGroups([...selectedGroups, group.id]);
                        } else {
                          setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
                      className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{group.name}</p>
                      <p className="text-sm text-gray-500">{group.contactCount} contact{group.contactCount !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-xs text-gray-400">
                      Click to add
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selectedGroups.length === 0 || isLoadingContacts}
            className={`px-4 py-2 rounded-md transition-colors ${
              selectedGroups.length > 0 && !isLoadingContacts
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoadingContacts ? 'Loading contacts...' : 'Add Selected Groups'}
          </button>
        </div>
      </div>
    </div>
  );
}; 